import { Box } from '@mui/material'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { Form } from 'react-final-form'
import { useParams } from 'react-router-dom'
import {
  Dossier_Bool_Exp,
  Kap_Program_Bool_Exp,
  Milestone,
  Mutation_Root,
  Project_Base_Bool_Exp,
  Query_Root,
} from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { resolveDefaultMilestoneRoot } from 'src/routing/routing-utils'
import {
  getMilestonesForDueDateQuery,
  updateMilestoneDueDateById,
} from 'src/screens/shared/implementation/details/milestoneQueries'
import { updateWorkflowDueDateQueryMutation } from 'src/screens/shared/implementation/details/milestoneWorkflowQueries'
import { SaveAndBackButton, SaveButton } from 'src/shared/button/Buttons'
import { DateTimePickerField } from 'src/shared/form/control/DateTimePickerField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { composeValidators, required, validDate, validDateInRange } from 'src/shared/form/validation/validators'
import { PageLayout } from 'src/shared/layout/PageLayout'
import { ScreenLayout } from 'src/shared/layout/ScreenLayout'
import { HelpAndInstructions } from 'src/shared/presentation/HelpAndInstructions'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useClient } from 'urql'
import { PROCESS } from 'src/shared/constants/constants'
import { Utils } from 'src/shared/utils/Utils'
import {
  getValidationDateRangeForMilestoneDueDate,
  ValidationForEditMilestoneDueDate,
} from 'src/screens/shared/implementation/details/milestone/MilestoneService'

interface Props {
  baseUrl: '/pf-kap' | '/pf-pgv' | '/kap'
}

type originType = 'SAVE' | 'SAVE_AND_BACK'

interface DueDateFormValues {
  dueDate?: string
}

export const MilestoneSummaryDueDateEditPage = ({ baseUrl }: Props): ReactElement => {
  const { getMessage } = useMessageSource()
  const originRef = useRef<originType>('SAVE')
  const notificationService = useNotificationService()

  const { projectId, programId, milestoneId, milestonePath } = useParams()

  const parsedMilestoneId = parseInt(milestoneId as string)
  const parsedProjectId = parseInt(projectId as string)
  const parsedProgramId = parseInt(programId as string)
  const processType = Utils.resolveProcess(baseUrl)

  const navigate = useDelayedNavigate()
  const urqlClient = useClient()

  const [milestone, setMilestone] = useState<Milestone | null>(null)
  const [dueDateValidation, setDueDateValidation] = useState<ValidationForEditMilestoneDueDate | null>(null)
  const [initialValues, setInitialValues] = useState<DueDateFormValues | null>(null)

  const fetchData = useCallback(async () => {
    const pfKapWhereClause: Project_Base_Bool_Exp = {
      pf_kap_projects: { id: { _eq: parsedProjectId } },
    }
    const pfPgvWhereClause: Project_Base_Bool_Exp = {
      pf_pgv_projects: { id: { _eq: parsedProjectId } },
    }
    const kapWhereClause: Kap_Program_Bool_Exp = {
      id: { _eq: parsedProgramId },
    }

    const dossierWhereClause: Dossier_Bool_Exp = {}

    if (processType === PROCESS.KAP) {
      dossierWhereClause.kap_program = kapWhereClause
    } else {
      dossierWhereClause.project_bases = processType === PROCESS.PF_KAP ? pfKapWhereClause : pfPgvWhereClause
    }

    // fetch data for the milestone validation and fetch actual milestone
    const milestonesData = await urqlClient
      .query<
        {
          milestone: Query_Root['milestone']
        },
        { dossierWhere: Dossier_Bool_Exp }
      >(getMilestonesForDueDateQuery, { dossierWhere: dossierWhereClause })
      .toPromise()

    const milestones = milestonesData.data?.milestone ?? []
    const currentMilestone = milestones.find((m) => m.id === parsedMilestoneId)

    if (milestones.length > 0 && currentMilestone) {
      const validation = getValidationDateRangeForMilestoneDueDate(currentMilestone, milestones, processType)
      if (validation != undefined) {
        setDueDateValidation(validation)
      }
    }

    if (currentMilestone && currentMilestone?.due_date && currentMilestone?.workflow_id) {
      setMilestone(currentMilestone)

      setInitialValues({
        dueDate: DateUtils.parseAndFormatDate(currentMilestone.due_date),
      })
    } else {
      notificationService.operationFailed()
    }
  }, [notificationService, parsedMilestoneId, urqlClient])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const onSubmit = (origin: originType) => (_: any) => {
    // trigger submit event
    if (originRef) {
      originRef.current = origin
    }
    document.getElementById('edit-form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))
  }

  const backHandler = () => {
    if (baseUrl === '/pf-pgv' || baseUrl === '/pf-kap') {
      navigate(resolveDefaultMilestoneRoot(baseUrl).nested.Summary.params({ projectId, milestoneId, milestonePath }))
    } else {
      const route = ROUTES.KapDefaultMilestoneRoot.nested.Summary.params({
        programId,
        milestoneId,
        milestonePath,
      })
      navigate(route)
    }
  }

  const updateHandler = async (values: DueDateFormValues) => {
    if (values.dueDate && milestone?.workflow_id) {
      // update field for dueDate
      const { data, error } = await urqlClient
        .mutation<{ update_milestone: Mutation_Root['update_milestone'] }>(updateMilestoneDueDateById, {
          milestoneId,
          dueDate: DateUtils.parseAndSerializeDate(values.dueDate),
        })
        .toPromise()

      if (error || data?.update_milestone?.affected_rows !== 1) {
        notificationService.operationFailed()
        return
      }

      // update workflow dueDate and taskDate
      const { data: dataMutation, error: errorMutation } = await urqlClient
        .mutation<{ updateWorkflowDueDate: Mutation_Root['updateWorkflowDueDate'] }>(
          updateWorkflowDueDateQueryMutation,
          {
            workflowId: milestone?.workflow_id,
            milestoneId: parsedMilestoneId,
            newDueDate: DateUtils.parseAndSerializeDate(values.dueDate),
          },
        )
        .toPromise()

      if (errorMutation || dataMutation?.updateWorkflowDueDate?.status === 'FAILURE') {
        notificationService.operationFailed()

        // Rollback the first mutation by resetting the dueDate
        await urqlClient
          .mutation<{ update_milestone: Mutation_Root['update_milestone'] }>(updateMilestoneDueDateById, {
            milestoneId,
            dueDate: milestone.due_date,
          })
          .toPromise()

        return
      }

      // finish
      setInitialValues({ ...initialValues, dueDate: values.dueDate })
      notificationService.changesSaved()
      if (originRef.current === 'SAVE_AND_BACK') {
        backHandler()
      }
    }
  }

  return (
    <ScreenLayout
      title={getMessage('label.title.edit.milestone.due-date')}
      onBack={backHandler}
      hasSecondLevelNavigation={false}
      actions={
        <>
          <SaveAndBackButton origin="header" onClick={onSubmit('SAVE_AND_BACK')} />
          <SaveButton origin="header" onClick={onSubmit('SAVE')} />
        </>
      }
    >
      <PageLayout>
        <Box>
          <HelpAndInstructions labelKey={`label.help.milestone.summary.edit.due-date`} defaultExpansion />

          {initialValues && (
            <Form<DueDateFormValues>
              initialValues={initialValues}
              onSubmit={updateHandler}
              render={({ handleSubmit }) => {
                return (
                  <form id="edit-form" onSubmit={handleSubmit}>
                    <DateTimePickerField
                      label={getMessage('label.due.date')}
                      name="dueDate"
                      minDate={dueDateValidation?.minDate}
                      maxDate={dueDateValidation?.maxDate}
                      validate={composeValidators(
                        required(),
                        validDate(),
                        validDateInRange(
                          dueDateValidation?.minDate,
                          dueDateValidation?.maxDate,
                          undefined,
                          dueDateValidation?.message,
                        ),
                      )}
                    />
                    <DirtyFormSpy />
                  </form>
                )
              }}
            />
          )}
        </Box>
      </PageLayout>
    </ScreenLayout>
  )
}
